import {
  buildNotifyBody,
  buildNotifyDeepLink,
  currentPermissionState,
  type NotifyScheduleEntry,
} from './NotifyService';

/**
 * Phase 8 chunk 5D — service-worker durability bridge. The page-side
 * setTimeout is the primary firing path while the tab is open; this
 * helper mirrors each arm/cancel into the SW so the SW's IndexedDB
 * store can fire the notification when the tab is closed. Silent on
 * platforms without an active SW (Vitest, denied registration, etc.).
 */
function postToServiceWorker(message: unknown): void {
  if (typeof navigator === 'undefined') return;
  const sw = navigator.serviceWorker;
  if (sw === undefined || sw === null) return;
  try {
    if (sw.controller !== null && sw.controller !== undefined) {
      sw.controller.postMessage(message);
      return;
    }
    void sw.ready.then((reg) => {
      if (reg.active !== null) reg.active.postMessage(message);
    });
  } catch {
    // No SW context — page-side setTimeout will still fire while open.
  }
}

function buildScheduleRecord(entry: NotifyScheduleEntry, leadMinutes: number) {
  return {
    key: scheduleKey(entry.norad, entry.riseAt),
    entry,
    leadMinutes,
    fireAtMs: entry.fireAtMs,
    deepLink: buildNotifyDeepLink(entry),
  };
}

/**
 * Phase 8 chunk 5C — in-page notification schedule store.
 *
 * Keeps a Map of armed `(norad, riseAt)` pass entries with their
 * corresponding {@link setTimeout} handles. Survives DetailPanel
 * mount/unmount cycles (the panel can re-render arbitrarily without
 * losing armed notifications) by living at module scope. Service-
 * worker durability for the tab-closed case is chunk 5D's problem.
 *
 * Pure DOM-light + Cesium-free so Vitest covers the scheduling math
 * with `vi.useFakeTimers()`.
 */

export type ScheduleKey = string;

export function scheduleKey(norad: number, riseAt: string): ScheduleKey {
  return `${norad}|${riseAt}`;
}

type Listener = (snapshot: ReadonlySet<ScheduleKey>) => void;

interface ArmedEntry {
  entry: NotifyScheduleEntry;
  timeoutId: ReturnType<typeof setTimeout>;
  leadMinutes: number;
}

class ScheduleStore {
  private armed = new Map<ScheduleKey, ArmedEntry>();
  private listeners = new Set<Listener>();
  /** Override hook so Vitest can stub the firing path. */
  private notifier: (entry: NotifyScheduleEntry, leadMinutes: number) => void = defaultNotifier;

  arm(entry: NotifyScheduleEntry, leadMinutes: number, now: number = Date.now()): boolean {
    const key = scheduleKey(entry.norad, entry.riseAt);
    if (this.armed.has(key)) return false;
    const delay = Math.max(0, entry.fireAtMs - now);
    const timeoutId = setTimeout(() => {
      this.armed.delete(key);
      this.fanout();
      // Tell the SW to forget the IDB row so its periodic scan won't
      // double-fire on top of our in-page Notification constructor.
      postToServiceWorker({ type: 'notify-cancel', key });
      try {
        this.notifier(entry, leadMinutes);
      } catch {
        // ignore — notifier should never throw, but we don't want to
        // leak a setTimeout failure into the UI.
      }
    }, delay);
    this.armed.set(key, { entry, timeoutId, leadMinutes });
    this.fanout();
    postToServiceWorker({ type: 'notify-arm', record: buildScheduleRecord(entry, leadMinutes) });
    return true;
  }

  cancel(norad: number, riseAt: string): boolean {
    const key = scheduleKey(norad, riseAt);
    const armed = this.armed.get(key);
    if (armed === undefined) return false;
    clearTimeout(armed.timeoutId);
    this.armed.delete(key);
    this.fanout();
    postToServiceWorker({ type: 'notify-cancel', key });
    return true;
  }

  isArmed(norad: number, riseAt: string): boolean {
    return this.armed.has(scheduleKey(norad, riseAt));
  }

  /** Returns a frozen snapshot of armed-pass keys. */
  snapshot(): ReadonlySet<ScheduleKey> {
    return new Set(this.armed.keys());
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => { this.listeners.delete(listener); };
  }

  /** Vitest-only: swap the notifier so we can observe firings. */
  __setNotifierForTests(fn: (entry: NotifyScheduleEntry, leadMinutes: number) => void): void {
    this.notifier = fn;
  }

  /** Vitest-only: tear down all timers. */
  __clearForTests(): void {
    for (const armed of this.armed.values()) {
      clearTimeout(armed.timeoutId);
    }
    this.armed.clear();
    this.listeners.clear();
    this.notifier = defaultNotifier;
  }

  private fanout(): void {
    const snap = this.snapshot();
    for (const listener of this.listeners) {
      try {
        listener(snap);
      } catch {
        // listeners must not throw; skip and continue
      }
    }
  }
}

/**
 * Default firing path — instantiates a `Notification` with the entry's
 * body text + a click handler that deep-links into the SPA.
 */
function defaultNotifier(entry: NotifyScheduleEntry, leadMinutes: number): void {
  if (currentPermissionState() !== 'granted') return;
  if (typeof Notification === 'undefined') return;
  try {
    const title = `${entry.satelliteName} pass in ${leadMinutes} min`;
    const body = buildNotifyBody(entry, leadMinutes);
    const n = new Notification(title, {
      body,
      icon: '/icons/icon-192.png',
      tag: `sat-trackr:${entry.norad}:${entry.riseAt}`,
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      const url = buildNotifyDeepLink(entry);
      try { window.location.href = url; } catch { /* ignore */ }
      n.close();
    };
  } catch {
    // notification constructor can throw under odd permission state
    // transitions; treat as a no-op rather than crashing the UI
  }
}

const sharedStore = new ScheduleStore();

export function getSharedScheduleStore(): ScheduleStore {
  return sharedStore;
}

export type { ScheduleStore };
